import Card from "@/components/common/Card";
import { Calendar } from "@/components/ui/calendar";
import type { holiday } from "@/interface/holiday.interface";
import {
  CalendarDays,
  Calendar as CalendarIcon,
  Info,
  Sparkles,
} from "lucide-react";

interface HolidayCalendarCardProps {
  holiday: holiday[];
  selectedDate: Date | undefined;
  setSelectedDate: (date: Date | undefined) => void;
  parseDate: (dateStr?: string) => Date;
  holidayDates: Date[];
  selectedHoliday: holiday | undefined;
  //Holidaylist
  today?: Date;
  todaysHoliday?: holiday;
  // clander
  calendarMonth: Date;
  onMonthChange: (date: Date) => void;
}

const HolidayCalendarCard: React.FC<HolidayCalendarCardProps> = ({
  holiday,
  selectedDate,
  setSelectedDate,
  parseDate,
  holidayDates,
  selectedHoliday,
  today,
  todaysHoliday,
  calendarMonth,
  onMonthChange,
}) => {
  return (
    <Card>
      <div className="space-y-6">
        <div className="flex items-center justify-between border-b pb-3.5 gap-2 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <CalendarIcon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-base tracking-tight">
                Holiday Calendar
              </h3>
              <p className="text-xs text-muted-foreground">
                Select a date to view holiday details
              </p>
            </div>
          </div>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
            {holiday.length} Holidays
          </span>
        </div>

        <div className="flex justify-center bg-muted/30 p-3 rounded-xl border border-border/50">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            month={calendarMonth} // controlled month
            onMonthChange={onMonthChange} // on click next/prev month
            defaultMonth={
              holiday.length > 0 ? parseDate(holiday[0].date) : new Date()
            }
            modifiers={{
              holiday: holidayDates,
              ...(today ? { today: [today] } : {}),
            }}
            modifiersClassNames={{
              holiday:
                "bg-red-500/15 text-red-600 dark:text-red-400 font-bold underline decoration-red-500 decoration-2 rounded-md hover:bg-red-500/25",
              ...(today
                ? {
                    today: todaysHoliday
                      ? "ring-2 ring-amber-400 ring-offset-1"
                      : "",
                  }
                : {}),
            }}
            className="p-1"
          />
        </div>

        <div className="flex items-center justify-around text-xs text-muted-foreground pt-1 border-t border-border/60">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-md bg-red-500/20 border border-red-500/50 inline-block"></span>
            <span>Official Holiday</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-md bg-primary inline-block"></span>
            <span>Selected Date</span>
          </div>
        </div>

        <div className="rounded-lg border bg-muted/40 p-3.5 transition-all duration-200">
          {selectedHoliday ? (
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0">
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="space-y-1 overflow-hidden">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-foreground">
                    {selectedHoliday.name}
                  </span>
                  <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-700 dark:text-amber-300">
                    Holiday
                  </span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {selectedHoliday.description}
                </p>
                <p className="flex items-center gap-1 text-[11px] font-medium text-primary pt-1">
                  <CalendarDays className="w-3 h-3" />
                  {selectedHoliday.date}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2.5 text-muted-foreground">
              <Info className="w-4 h-4 shrink-0 text-slate-400" />
              <span className="text-xs">
                {selectedDate
                  ? `No holiday on ${selectedDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}`
                  : "Click any date on the calendar to inspect"}
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default HolidayCalendarCard;
